import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { STATUSES } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { StatusTimeline } from "@/components/StatusTimeline";
import { ReferralInfo } from "@/pages/ReferralDetail";

export const ReferralAdminDialog = ({ referral: r, categories, onClose, onSaved }) => {
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (r) { setStatus(r.status); setNote(""); }
  }, [r]);

  const run = async (kind, fn, msg) => {
    setBusy(kind);
    try {
      const { data } = await fn();
      toast.success(msg);
      setNote("");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto rounded-3xl bg-white p-5 sm:p-7" data-testid="admin-referral-dialog">
        {r && (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-3 text-left font-display text-2xl">{r.lead_name} <StatusBadge status={r.status} testId="dialog-current-status" /></DialogTitle>
              <p className="text-left text-xs text-[#75867E]">Segnalata da {r.user_name} ({r.user_email})</p>
            </DialogHeader>
            <ReferralInfo r={r} />

            <div className="space-y-2 rounded-2xl bg-[#F4F5F0] p-4">
              <Label>Correggi tipologia</Label>
              <Select value={r.category_id} onValueChange={(v) => run("cat", () => api.put(`/admin/referrals/${r.referral_id}/category`, { category_id: v }), "Tipologia aggiornata")}>
                <SelectTrigger className="h-11 bg-white" data-testid="dialog-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}{!c.active && " (disattivata)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-3 rounded-2xl bg-[#F4F5F0] p-4">
              <Label>Cambia stato</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-11 bg-white" data-testid="dialog-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUSES).map(([k, s]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea rows={2} placeholder="Nota (visibile nello storico)" className="bg-white" value={note} onChange={(e) => setNote(e.target.value)} data-testid="dialog-status-note" />
              <Button disabled={!!busy || (status === r.status && !note.trim())} onClick={() => run("st", () => api.put(`/admin/referrals/${r.referral_id}/status`, { status, note }), "Stato aggiornato")}
                className="h-11 w-full rounded-full bg-[#0F2D5E] hover:bg-[#0B2250] sm:w-auto" data-testid="dialog-save-status-btn">
                {busy === "st" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva stato
              </Button>
            </div>

            <div>
              <h3 className="mb-4 font-bold">Storico</h3>
              <StatusTimeline history={r.history} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
